import React, { useState, useRef } from 'react';
import { X, Printer, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { formatDate } from '@/utils/formatters';

const CertificateOfConformance = ({ order, partDetails, isOpen, onClose }) => {
  const printRef = useRef();
  const [inspectionChecks, setInspectionChecks] = useState({
    gauging: false,
    dimensional: false,
    visual: false
  });

  // Generate certificate number
  const certificateNumber = `${order?.order_number}-COC`;

  // Extract process data
  const getProcessData = (processName, field) => {
    if (!order?.process_data) return null;
    
    // Check for exact process name match first
    if (order.process_data[processName]?.[field]) {
      return order.process_data[processName][field];
    }
    
    // Check all process data entries for the field
    for (const [key, data] of Object.entries(order.process_data)) {
      if (key.toLowerCase().includes(processName.toLowerCase()) && data[field]) {
        return data[field];
      }
    }
    return null;
  };

  // Get completion date
  const getCompletionDate = () => {
    if (order?.completed_at) {
      return formatDate(order.completed_at);
    }
    return formatDate(new Date().toISOString());
  };

  // Certificate data with validation
  const certificateData = {
    partDescription: partDetails?.description || null,
    partNumber: order?.part_number || null,
    drawingNumber: partDetails?.drawing_number || null,
    customer: order?.customer || null,
    customerOrderNumber: order?.customer_order_number || null,
    worksOrderNumber: order?.order_number || null,
    dateOfIssue: getCompletionDate(),
    certificateNumber: certificateNumber,
    quantity: order?.quantity || null,
    
    // Raw Material Information
    materialGrade: partDetails?.material_grade || null,
    castNumber: getProcessData('Cutting', 'cast_number') || getProcessData('Material', 'cast_number') || null,
    binNumber: getProcessData('Cutting', 'bundle_number') || null,
    
    // Heat Treatment Information
    heatTreatmentSpec: partDetails?.heat_treatment || null,
    heatTreatmentSupplier: getProcessData('Heat Treatment', 'supplier') || null,
    heatTreatmentCertNumber: getProcessData('Heat Treatment', 'certificate_number') || getProcessData('Heat Treatment', 'po_number') || null,
    
    // Surface Coating Information
    surfaceCoatingSpec: getProcessData('Surface Coating', 'specification') || null,
    surfaceCoatingSupplier: getProcessData('Surface Coating', 'supplier') || null,
    
    // Dimensional Inspection
    inspectionChecks: inspectionChecks
  };

  // Check for missing required fields
  const getMissingFields = () => {
    const missing = [];
    if (!certificateData.partNumber) missing.push('Part Number');
    if (!certificateData.materialGrade) missing.push('Material Grade');
    if (!certificateData.castNumber) missing.push('Cast Number');
    
    // Only check heat treatment if it's required (not "None")
    if (certificateData.heatTreatmentSpec && certificateData.heatTreatmentSpec !== 'None' && !certificateData.heatTreatmentCertNumber) {
      missing.push('Heat Treatment Certificate Number');
    }
    
    // Check if at least one inspection is selected
    if (!inspectionChecks.gauging && !inspectionChecks.dimensional && !inspectionChecks.visual) {
      missing.push('Dimensional Inspection (select at least one)');
    }
    
    return missing;
  };

  const missingFields = getMissingFields();
  const canPrint = missingFields.length === 0;

  const handlePrint = () => {
    if (!canPrint) return;
    
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Pop-up blocked. Please allow pop-ups.'); return; }

    const doc = printWindow.document;
    doc.open();

    const style = doc.createElement('style');
    style.textContent = `
      @page { size: A4; margin: 10mm; }
      body { font-family: Arial, sans-serif; color: #000; background: #fff; margin: 0; padding: 0; font-size: 11px; }
      .certificate { max-width: 210mm; margin: 0 auto; padding: 5mm; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 2px solid #dc2626; }
      .header-title { font-size: 28px; font-style: italic; font-weight: normal; color: #000; line-height: 1.2; }
      .header-logo { text-align: right; }
      .header-logo img { height: 70px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
      td, th { border: 1px solid #000; padding: 6px 8px; text-align: left; vertical-align: top; }
      .label { font-weight: bold; background: #f5f5f5; width: 25%; }
      .value { width: 25%; }
      .section-header { background: #e5e5e5; font-weight: bold; text-align: center; padding: 8px; font-size: 12px; }
      .signature-section { margin-top: 20px; text-align: center; }
      .signature-line { border-top: 1px solid #000; width: 300px; margin: 30px auto 5px auto; padding-top: 5px; }
      .yn-cell { width: 50px; text-align: center; }
      .missing { color: #dc2626; font-style: italic; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    `;

    doc.head.appendChild(style);
    doc.title = `Certificate of Conformance - ${order?.order_number || ''}`;
    doc.body.innerHTML = printContent.innerHTML;
    doc.close();
    
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  if (!order) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-white uppercase tracking-tight text-xl">
              Certificate of Conformance
            </DialogTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Missing Fields Warning */}
        {missingFields.length > 0 && (
          <div className="bg-yellow-950/50 border border-yellow-600 rounded-sm p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-500 font-bold uppercase text-sm mb-2">Missing Information</p>
                <ul className="text-yellow-400 text-sm space-y-1">
                  {missingFields.map((field, idx) => (
                    <li key={idx}>• {field}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Dimensional Inspection Selection */}
        <div className="bg-muted/30 border border-border rounded-sm p-4 mb-4">
          <Label className="text-white uppercase tracking-wider text-xs mb-3 block">
            Dimensional Inspection (Select applicable)
          </Label>
          <div className="flex gap-6">
            <div className="flex items-center gap-2">
              <Checkbox 
                id="gauging"
                checked={inspectionChecks.gauging}
                onCheckedChange={(checked) => setInspectionChecks(prev => ({ ...prev, gauging: checked }))}
              />
              <Label htmlFor="gauging" className="text-white cursor-pointer">Gauged</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="dimensional"
                checked={inspectionChecks.dimensional}
                onCheckedChange={(checked) => setInspectionChecks(prev => ({ ...prev, dimensional: checked }))}
              />
              <Label htmlFor="dimensional" className="text-white cursor-pointer">Dimensional Inspect</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="visual"
                checked={inspectionChecks.visual}
                onCheckedChange={(checked) => setInspectionChecks(prev => ({ ...prev, visual: checked }))}
              />
              <Label htmlFor="visual" className="text-white cursor-pointer">Visual</Label>
            </div>
          </div>
        </div>

        {/* Certificate Preview */}
        <div className="bg-white text-black rounded-sm p-6" ref={printRef}>
          <div className="certificate">
            {/* Header */}
            <div className="header">
              <div className="header-title">
                Certificate Of<br/>Conformance
              </div>
              <div className="header-logo">
                <img src="/hotforge-logo.png" alt="Hotforge" style={{ height: '70px' }} />
              </div>
            </div>

            {/* Main Info Table */}
            <table>
              <tbody>
                <tr>
                  <td className="label">Certificate Nr</td>
                  <td className="value">{certificateData.certificateNumber}</td>
                  <td className="label">Date</td>
                  <td className="value">{certificateData.dateOfIssue}</td>
                </tr>
                <tr>
                  <td className="label">Part Number</td>
                  <td className="value">{certificateData.partNumber || <span className="missing">Missing</span>}</td>
                  <td className="label">Description</td>
                  <td className="value">{certificateData.partDescription || '-'}</td>
                </tr>
                <tr>
                  <td className="label">Drawing Number</td>
                  <td className="value">{certificateData.drawingNumber || '-'}</td>
                  <td className="label">Quantity</td>
                  <td className="value">{certificateData.quantity}</td>
                </tr>
                <tr>
                  <td className="label">Customer</td>
                  <td className="value">{certificateData.customer || '-'}</td>
                  <td className="label">Customer Order Nr</td>
                  <td className="value">{certificateData.customerOrderNumber || '-'}</td>
                </tr>
              </tbody>
            </table>

            {/* Signature */}
            <div className="signature-section">
              <div className="signature-line">
                on behalf of HotForge (Pty) Ltd
              </div>
            </div>

            {/* Raw Material Information */}
            <table style={{ marginTop: '15px' }}>
              <tbody>
                <tr>
                  <td colSpan="2" className="section-header">Raw Material Information</td>
                </tr>
                <tr>
                  <td className="label">Material Grade</td>
                  <td className="value">{certificateData.materialGrade || <span className="missing">Missing</span>}</td>
                </tr>
                <tr>
                  <td className="label">Cast Number</td>
                  <td className="value">{certificateData.castNumber || <span className="missing">Missing</span>}</td>
                </tr>
                <tr>
                  <td className="label">Bin Number</td>
                  <td className="value">{certificateData.binNumber || '-'}</td>
                </tr>
              </tbody>
            </table>

            {/* Heat Treatment */}
            <table style={{ marginTop: '10px' }}>
              <tbody>
                <tr>
                  <td colSpan="2" className="section-header">Heat Treatment</td>
                </tr>
                <tr>
                  <td className="label">Supplier</td>
                  <td className="value">{certificateData.heatTreatmentSupplier || '-'}</td>
                </tr>
                <tr>
                  <td className="label">Heat Treatment</td>
                  <td className="value">{certificateData.heatTreatmentSpec || 'None'}</td>
                </tr>
                <tr>
                  <td className="label">Certificate Number</td>
                  <td className="value">
                    {certificateData.heatTreatmentSpec && certificateData.heatTreatmentSpec !== 'None' 
                      ? (certificateData.heatTreatmentCertNumber || <span className="missing">Missing</span>)
                      : 'N/A'}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Surface Coating */}
            {(certificateData.surfaceCoatingSpec || certificateData.surfaceCoatingSupplier) && (
              <table style={{ marginTop: '10px' }}>
                <tbody>
                  <tr>
                    <td colSpan="2" className="section-header">Surface Coating</td>
                  </tr>
                  <tr>
                    <td className="label">Supplier</td>
                    <td className="value">{certificateData.surfaceCoatingSupplier || '-'}</td>
                  </tr>
                  <tr>
                    <td className="label">Specification</td>
                    <td className="value">{certificateData.surfaceCoatingSpec || '-'}</td>
                  </tr>
                </tbody>
              </table>
            )}

            {/* Dimensional Inspection */}
            <table style={{ marginTop: '10px' }}>
              <tbody>
                <tr>
                  <td colSpan="2" className="section-header">Dimensional Inspection</td>
                </tr>
                <tr>
                  <td className="label">Gauged</td>
                  <td className="yn-cell">{inspectionChecks.gauging ? 'Y' : 'N'}</td>
                </tr>
                <tr>
                  <td className="label">Dimensional Inspect</td>
                  <td className="yn-cell">{inspectionChecks.dimensional ? 'Y' : 'N'}</td>
                </tr>
                <tr>
                  <td className="label">Visual</td>
                  <td className="yn-cell">{inspectionChecks.visual ? 'Y' : 'N'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mt-4">
          <Button
            onClick={handlePrint}
            disabled={!canPrint}
            className={`flex-1 h-12 font-bold uppercase tracking-wide flex items-center justify-center gap-2 ${
              canPrint 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-muted cursor-not-allowed'
            }`}
          >
            {canPrint ? (
              <>
                <Printer className="w-5 h-5" />
                Print Certificate
              </>
            ) : (
              <>
                <AlertTriangle className="w-5 h-5" />
                Complete Missing Information
              </>
            )}
          </Button>
          <Button variant="outline" onClick={onClose} className="border-border hover:border-primary">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CertificateOfConformance;
